"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { Plus, Send, ImageIcon, Video, Edit3, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import eventBus from "../../utils/eventBus";

interface FabConfig {
  icon: string;
  text?: string;
  action: () => void;
}

type GeneratorType = "image" | "video" | "image-editing" | "effects";

interface GeneratorOption {
  type: GeneratorType;
  icon: React.ReactNode;
  label: string;
  color: string;
}

const CreateFAB: React.FC = () => {
  const [config, setConfig] = useState<FabConfig | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isCreatePage = location.pathname === "/create";

  const generatorOptions: GeneratorOption[] = [
    {
      type: "image",
      icon: <ImageIcon className="w-5 h-5" />,
      label: "Image",
      color: "from-blue-500 to-blue-600",
    },
    {
      type: "video",
      icon: <Video className="w-5 h-5" />,
      label: "Video",
      color: "from-red-500 to-red-600",
    },
    {
      type: "image-editing",
      icon: <Edit3 className="w-5 h-5" />,
      label: "Edit",
      color: "from-green-500 to-green-600",
    },
    {
      type: "effects",
      icon: <Sparkles className="w-5 h-5" />,
      label: "Effect",
      color: "from-yellow-500 to-yellow-600",
    },
  ];

  const defaultConfig: FabConfig = {
    icon: "Plus",
    action: () => setIsExpanded(!isExpanded), // Toggle expansion instead of empty action
  };

  useEffect(() => {
    const handleConfig = (newConfig: FabConfig) => setConfig(newConfig);
    const handleReset = () => setConfig(null);

    eventBus.on("fab:config", handleConfig);
    eventBus.on("fab:reset", handleReset);

    return () => {
      eventBus.off("fab:config", handleConfig);
      eventBus.off("fab:reset", handleReset);
    };
  }, []);

  useEffect(() => {
    setIsExpanded(false);
  }, [location.pathname]);

  const currentConfig = config && isCreatePage ? config : defaultConfig;

  const handleGeneratorSelect = (generatorType: GeneratorType) => {
    setIsExpanded(false);
    navigate("/create", {
      state: {
        activeGenerator: generatorType,
      },
    });
  };

  const fabContent = {
    initial: { scale: 0, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0, opacity: 0 },
    transition: { type: "spring", stiffness: 400, damping: 15 },
  };

  const renderIcon = () => {
    switch (currentConfig.icon) {
      case "Send":
      case "generate":
        return <Send className="w-6 h-6 text-white" />;
      default:
        return (
          <motion.div
            animate={{ rotate: isExpanded ? 45 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <Plus className="w-8 h-8 text-white" />
          </motion.div>
        );
    }
  };

  const FabButton = () => (
    <motion.button
      onClick={currentConfig.action}
      className={`${currentConfig.text ? "w-auto px-6" : "w-16"} h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300`}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      {renderIcon()}
      {currentConfig.text && (
        <span className="ml-2 text-white font-semibold">
          {currentConfig.text}
        </span>
      )}
    </motion.button>
  );

  return (
    <motion.div
      className="md:hidden fixed bottom-24 right-6 z-20"
      variants={fabContent}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <AnimatePresence mode="wait">
        {currentConfig.icon === "Plus" && location.pathname !== "/create" ? (
          <div className="relative">
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  className="absolute bottom-20 right-0 flex flex-col gap-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.1 }}
                >
                  {generatorOptions.map((option, index) => (
                    <motion.button
                      key={option.type}
                      onClick={() => handleGeneratorSelect(option.type)}
                      className={`w-14 h-14 bg-gradient-to-r ${option.color} rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 text-white`}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{
                        delay: index * 0.02,
                        type: "spring",
                        stiffness: 600,
                        damping: 15,
                      }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      {option.icon}
                    </motion.button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  className="fixed inset-0 bg-black bg-opacity-20 -z-10"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.1 }}
                  onClick={() => setIsExpanded(false)}
                />
              )}
            </AnimatePresence>

            <FabButton />
          </div>
        ) : location.pathname === "/create" && currentConfig.icon !== "Plus" ? (
          <FabButton />
        ) : null}
      </AnimatePresence>
    </motion.div>
  );
};

export default CreateFAB;
